/**
 * The half-circle dials of the Overview (DESIGN UD-36, ED-181 to ED-185). One geometry for both: a 0 to 100 scale swept
 * over 180 degrees, left end low, right end high. The authorship dial carries the reference mark, the length group's index
 * ceiling and the text-sampling interval; the translation dial is plainer (no marks, no interval). The graphic is
 * `role="img"` with the reading and its conditions as its name; the visible text beside it says the same. The needle turns
 * with the `--dur-calm` transition, which `tokens.css` sets to 0 under reduced motion.
 */
import type { DialMark } from '../../shared/dial.ts';

const CX = 100;
const CY = 100;
const R = 78;
const STEPS = ['--ev-100', '--ev-200', '--ev-300', '--ev-400', '--ev-500', '--ev-600', '--ev-700'];

/** A point of the scale (0 to 100) at radius `r`: the left end is (CX - r, CY), the right end (CX + r, CY). */
function at(v: number, r: number): [number, number] {
	const a = Math.PI * (1 - Math.min(100, Math.max(0, v)) / 100);
	return [CX + r * Math.cos(a), CY - r * Math.sin(a)];
}

/** An arc of the scale from `a` to `b` at radius `r` (clockwise on screen: left over the top to the right). */
function arc(a: number, b: number, r: number): string {
	const [x1, y1] = at(a, r);
	const [x2, y2] = at(b, r);
	return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

export interface DialProps {
	/** Used in test ids and element ids. */
	id: string;
	/** The needle's rotation from the left end, in degrees; null draws the greyed dial (no needle). */
	degrees: number | null;
	aria: string;
	/** Left and right end labels. */
	lowEnd: string;
	highEnd: string;
	marks?: DialMark[];
	interval?: [number, number] | null;
	/** The smaller dial of the translation index. */
	small?: boolean;
}

export function Dial({
	id,
	degrees,
	aria,
	lowEnd,
	highEnd,
	marks = [],
	interval = null,
	small,
}: DialProps) {
	const grey = degrees === null;
	const seg = 100 / STEPS.length;
	return (
		<figure
			class={`dial${small ? ' dial-small' : ''}${grey ? ' is-grey' : ''}`}
			data-testid={`${id}-dial`}
		>
			<svg viewBox="0 0 200 112" role="img" aria-label={aria} focusable="false">
				<title>{aria}</title>
				{STEPS.map((tok, i) => (
					<path
						key={tok}
						d={arc(i * seg, (i + 1) * seg, R)}
						fill="none"
						stroke={grey ? 'var(--stone-300)' : `var(${tok})`}
						stroke-width="14"
					/>
				))}
				{interval && !grey ? (
					<path
						data-testid={`${id}-dial-interval`}
						d={arc(interval[0], interval[1], R + 13)}
						fill="none"
						stroke="var(--ink-700)"
						stroke-width="5"
						stroke-linecap="butt"
						opacity="0.55"
					/>
				) : null}
				{[0, 50, 100].map((v) => {
					const [x1, y1] = at(v, R - 9);
					const [x2, y2] = at(v, R + 9);
					return (
						<line
							key={v}
							x1={x1}
							y1={y1}
							x2={x2}
							y2={y2}
							stroke="var(--ink-600)"
							stroke-width={v === 50 ? 1 : 1.5}
							opacity={v === 50 ? 0.5 : 1}
						/>
					);
				})}
				{marks.map((m) => {
					const [x1, y1] = at(m.value, R - 14);
					const [x2, y2] = at(m.value, R + 12);
					return (
						<line
							key={m.id}
							data-testid={`${id}-dial-mark-${m.id}`}
							data-value={m.value.toFixed(1)}
							x1={x1}
							y1={y1}
							x2={x2}
							y2={y2}
							stroke={m.id === 'reference' ? 'var(--cat-2)' : 'var(--status-notice)'}
							stroke-width="2.5"
							stroke-dasharray={m.id === 'ceiling' ? '3 2' : undefined}
						/>
					);
				})}
				{grey ? null : (
					<g
						class="dial-needle"
						data-testid={`${id}-dial-needle`}
						data-degrees={degrees.toFixed(1)}
						style={`transform: rotate(${degrees}deg); transform-origin: ${CX}px ${CY}px`}
					>
						<line
							x1={CX - 6}
							y1={CY}
							x2={CX - R + 6}
							y2={CY}
							stroke="var(--ink-900)"
							stroke-width="3"
							stroke-linecap="round"
						/>
					</g>
				)}
				<circle cx={CX} cy={CY} r="6" fill={grey ? 'var(--stone-500)' : 'var(--ink-900)'} />
			</svg>
			<figcaption class="dial-ends" aria-hidden="true">
				<span>{lowEnd}</span>
				<span>{highEnd}</span>
			</figcaption>
		</figure>
	);
}
